<template>
  <div class="search-container">
    <div class="search-wrapper">
      <div class="search-input-container">
        <input 
          :value="searchQuery"
          type="text" 
          placeholder="Search messages..." 
          class="search-input"
          @input="$emit('update:searchQuery', $event.target.value)"
        />
        <div v-if="searchQuery" class="search-info">
          {{ resultCount }} results
        </div>
      </div>
    </div>
    <div class="controls-container">
      <select 
        :value="searchMode"
        class="search-mode-select" 
        @change="$emit('update:searchMode', $event.target.value)"
      >
        <option value="contains">Contains</option>
        <option value="wholeWord">Whole Word</option>
        <option value="regex">Regex</option>
      </select>
      <input 
        type="text" 
        :value="startDate"
        class="date-input"
        placeholder="MM/DD/YYYY"
        @input="$emit('update:startDate', $event.target.value)"
        @change="$emit('dateRangeChange')"
      />
      <span class="date-separator">to</span>
      <input 
        type="text" 
        :value="endDate"
        class="date-input"
        placeholder="MM/DD/YYYY"
        @input="$emit('update:endDate', $event.target.value)"
        @change="$emit('dateRangeChange')"
      />
      <button class="clear-button" @click="$emit('clearFilters')">Clear All</button>
      <button 
        class="share-button" 
        @click="shareSearch"
        :disabled="!searchQuery"
        :class="{ 'disabled': !searchQuery }"
      >Share Search</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBar',
  props: {
    searchQuery: {
      type: String,
      required: true
    },
    searchMode: {
      type: String,
      required: true
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    resultCount: {
      type: Number,
      required: true
    }
  },
  emits: [
    'update:searchQuery',
    'update:searchMode',
    'update:startDate',
    'update:endDate',
    'dateRangeChange',
    'clearFilters'
  ],
  methods: {
    shareSearch() {
      const params = new URLSearchParams();
      if (this.searchQuery) params.append('q', this.searchQuery);
      if (this.searchMode !== 'contains') params.append('mode', this.searchMode);
      if (this.startDate) params.append('start', this.startDate);
      if (this.endDate) params.append('end', this.endDate);
      
      const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      
      if (navigator.share) {
        navigator.share({
          title: 'Basta Chats Search',
          text: 'Check out this search in Basta Chats',
          url: shareUrl
        }).catch(console.error);
      } else {
        navigator.clipboard.writeText(shareUrl)
          .then(() => {
            alert('Search URL copied to clipboard!');
          })
          .catch(console.error);
      }
    }
  }
}
</script>

<style scoped>
.search-container {
  margin-bottom: 2rem;
}

.search-wrapper {
  margin-bottom: 0.5rem;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  padding-right: 120px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-primary);
  font-size: 1rem;
}

.search-input::placeholder {
  color: var(--text-secondary);
}

.search-info {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.controls-container {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-mode-select {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  width: 150px;
}

.date-input {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-primary);
  font-size: 0.9rem;
  width: 150px;
}

.date-input::placeholder {
  color: var(--text-secondary);
}

.date-separator {
  color: var(--text-secondary);
}

.clear-button {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: rgba(26, 26, 26, 0.8);
  color: var(--text-primary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.clear-button:hover {
  background-color: var(--primary);
  border-color: var(--primary);
}

.share-button {
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--primary);
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.share-button:hover:not(.disabled) {
  background-color: #0180e0;
  border-color: #0180e0;
}

.share-button.disabled {
  background-color: rgba(26, 26, 26, 0.8);
  border-color: var(--border);
  color: var(--text-secondary);
  cursor: not-allowed;
  opacity: 0.7;
}

@media (max-width: 768px) {
  .controls-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .search-mode-select,
  .date-input,
  .clear-button,
  .share-button {
    width: 100%;
  }

  .date-separator {
    text-align: center;
    margin: 0.25rem 0;
  }
}
</style> 